import React, { useCallback, useState, useEffect } from 'react'
import { hooks, I18n } from '@front/volcanion'

import Component from './component'
import Callbacks from './callbacks'

const Container = ({ model_name, ...props }) => {
  const [activeFilterKey, setActiveFilterKey] = useState(null)
  const reload = hooks.useGlobalFormFunction(`${model_name}_search`, 'reload')
  const onSubmit = hooks.useGlobalFormFunction(`${model_name}_search`, 'requestSubmit')
  const reset = hooks.useGlobalFormFunction(`${model_name}_search`, 'reset')
  const formState = hooks.useGlobalFormState(`${model_name}_search`)

  const features = hooks.useRelayFeatures([
    'FILTER_ACTIVE',
    'FILTER_RESERVATION',
    'FILTER_NOT_LOADED',
    'FILTER_TODAY',
    'FILTER_TODAY_PLUS_ONE',
    'FILTER_TODAY_PLUS_TWO',
    'FILTER_TODAY_CREATED',
    'FILTER_TODAY_CANCELED',
    'FILTER_GREATER_THAN_THREE',
    'FILTER_PROVINCES'
  ])

  const [current_city] = hooks.useActiveUser({
    populate: ['staffinfo'],
    mutator: 'staffinfo.city'
  })

  const [not_loaded_reason_id] = hooks.useModelSearch('reason', 'get', {
    mutator: reason => reason.reason_id,
    initial_filter: {},
    forced_filter: { identifier: "not_loaded" },
    single: true
  })

  const [province_ids] = hooks.useModelSearch('city', 'get', {
    mutator: 'city_id',
    initial_filter: {},
    forced_filter: { city_id: { '!': current_city } },
    validate_filter: useCallback(() => !!features?.FILTER_PROVINCES && !!current_city, [features?.FILTER_PROVINCES, current_city]),
    watchers: [features?.FILTER_PROVINCES, current_city]
  })

  const onFilterChange = useCallback(Callbacks.onFilterChangeHandler(reset, reload, onSubmit), [reset, reload, onSubmit])

  const handleActiveFilter = useCallback(Callbacks.handleActiveFilterHandler(onFilterChange), [onFilterChange])
  const handleReservationFilter = useCallback(Callbacks.handleReservationFilterHandler(onFilterChange), [onFilterChange])
  const handleNotLoadedFilter = useCallback(Callbacks.handleNotLoadedFilterHandler(onFilterChange, not_loaded_reason_id), [onFilterChange, not_loaded_reason_id])
  const handleTodayFilter = useCallback(Callbacks.handleTodayFilterHandler(onFilterChange), [onFilterChange])
  const handleTodayPlusOneFilter = useCallback(Callbacks.handleTodayPlusOneFilterHandler(onFilterChange), [onFilterChange])
  const handleTodayPlusTwoFilter = useCallback(Callbacks.handleTodayPlusTwoFilterHandler(onFilterChange), [onFilterChange])
  const handleTodayCreatedFilter = useCallback(Callbacks.handleTodayCreatedFilterHandler(onFilterChange), [onFilterChange])
  const handleTodayCanceledFilter = useCallback(Callbacks.handleTodayCanceledFilterHandler(onFilterChange), [onFilterChange])
  const handleGreaterThanThreeFilter = useCallback(Callbacks.handleGreaterThanThreeFilterHandler(onFilterChange), [onFilterChange])
  const handleProvincesFilter = useCallback(Callbacks.handleProvincesFilterHandler(onFilterChange, province_ids), [onFilterChange, province_ids])

  const createFilterHandler = useCallback(Callbacks.createFilterHandlerHandler(setActiveFilterKey), [setActiveFilterKey])
  const activeFilter = useCallback(createFilterHandler('active', handleActiveFilter), [createFilterHandler, handleActiveFilter])
  const reservationFilter = useCallback(createFilterHandler('reservation', handleReservationFilter), [createFilterHandler, handleReservationFilter])
  const notLoadedFilter = useCallback(createFilterHandler('notLoaded', handleNotLoadedFilter), [createFilterHandler, handleNotLoadedFilter])
  const todayFilter = useCallback(createFilterHandler('today', handleTodayFilter), [createFilterHandler, handleTodayFilter])
  const todayPlusOneFilter = useCallback(createFilterHandler('todayPlusOne', handleTodayPlusOneFilter), [createFilterHandler, handleTodayPlusOneFilter])
  const todayPlusTwoFilter = useCallback(createFilterHandler('todayPlusTwo', handleTodayPlusTwoFilter), [createFilterHandler, handleTodayPlusTwoFilter])
  const todayCreatedFilter = useCallback(createFilterHandler('todayCreated', handleTodayCreatedFilter), [createFilterHandler, handleTodayCreatedFilter])
  const todayCanceledFilter = useCallback(createFilterHandler('todayCanceled', handleTodayCanceledFilter), [createFilterHandler, handleTodayCanceledFilter])
  const greaterThanThreeFilter = useCallback(createFilterHandler('greaterThanThree', handleGreaterThanThreeFilter), [createFilterHandler, handleGreaterThanThreeFilter])
  const provincesFilter = useCallback(createFilterHandler('provinces', handleProvincesFilter), [createFilterHandler, handleProvincesFilter])

  const actions = [
    { label: I18n.t('order.filter.active'), key: 'active', onClick: activeFilter, isActive: activeFilterKey === 'active', hide: !features?.FILTER_ACTIVE },
    { label: I18n.t('order.filter.reservation'), key: 'reservation', onClick: reservationFilter, isActive: activeFilterKey === 'reservation', hide: !features?.FILTER_RESERVATION },
    { label: I18n.t('order.filter.not_loaded'), key: 'notLoaded', onClick: notLoadedFilter, isActive: activeFilterKey === 'notLoaded', hide: !features?.FILTER_NOT_LOADED },
    { label: I18n.t('order.filter.today'), key: 'today', onClick: todayFilter, isActive: activeFilterKey === 'today', hide: !features?.FILTER_TODAY },
    { label: I18n.t('order.filter.today_plus_one'), key: 'todayPlusOne', onClick: todayPlusOneFilter, isActive: activeFilterKey === 'todayPlusOne', hide: !features?.FILTER_TODAY_PLUS_ONE },
    { label: I18n.t('order.filter.today_plus_two'), key: 'todayPlusTwo', onClick: todayPlusTwoFilter, isActive: activeFilterKey === 'todayPlusTwo', hide: !features?.FILTER_TODAY_PLUS_TWO },
    { label: I18n.t('order.filter.today_created'), key: 'todayCreated', onClick: todayCreatedFilter, isActive: activeFilterKey === 'todayCreated', hide: !features?.FILTER_TODAY_CREATED },
    { label: I18n.t('order.filter.today_canceled'), key: 'todayCanceled', onClick: todayCanceledFilter, isActive: activeFilterKey === 'todayCanceled', hide: !features?.FILTER_TODAY_CANCELED },
    { label: I18n.t('order.filter.greater_than_three'), key: 'greaterThanThree', onClick: greaterThanThreeFilter, isActive: activeFilterKey === 'greaterThanThree', hide: !features?.FILTER_GREATER_THAN_THREE },
    { label: I18n.t('order.filter.provinces'), key: 'provinces', onClick: provincesFilter, isActive: activeFilterKey === 'provinces', hide: !features?.FILTER_PROVINCES }
  ]

  useEffect(() => {
    if (formState?.values && Object.keys(formState?.values).length === 0) {
      setActiveFilterKey(null)
    }
  }, [formState?.values])


  const mergedProps = {
    actions
  }

  return <Component {...mergedProps} {...props} />
}

export default Container
